import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { patientAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function EditPatientPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', geburtsdatum: '', versicherung: '', pflegegrad: 0,
    adresse: '', telefon: '', status: 'aktiv', notizen: '', team: '',
    diagnosen: '', versorgungsbeginn: ''
  });

  useEffect(() => {
    loadPatient();
  }, [id]);

  const loadPatient = async () => {
    setLoading(true);
    try {
      const res = await patientAPI.get(id);
      const p = res.data;
      setForm({
        name: p.name || '',
        geburtsdatum: p.geburtsdatum || '',
        versicherung: p.versicherung || '',
        pflegegrad: p.pflegegrad || 0,
        adresse: p.adresse || '',
        telefon: p.telefon || '',
        status: p.status || 'aktiv',
        notizen: p.notizen || '',
        team: p.team || '',
        diagnosen: p.diagnosen || '',
        versorgungsbeginn: p.versorgungsbeginn || ''
      });
    } catch (err) {
      toast.error('Patient nicht gefunden');
      navigate('/patienten');
    } finally {
      setLoading(false);
    }
  };

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.geburtsdatum) {
      toast.error('Name und Geburtsdatum sind Pflichtfelder');
      return;
    }
    setSaving(true);
    try {
      await patientAPI.update(id, form);
      toast.success('Patient erfolgreich aktualisiert');
      navigate(`/patienten/${id}`);
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto space-y-4">
        <div className="h-12 bg-slate-100 rounded-xl animate-pulse" />
        <div className="h-96 bg-slate-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="edit-patient-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(`/patienten/${id}`)} className="p-2 rounded-xl" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-xl md:text-2xl font-heading font-bold text-slate-900">Patient bearbeiten</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base">Stammdaten</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input data-testid="patient-name-input" value={form.name} onChange={e => update('name', e.target.value)} placeholder="Vor- und Nachname" className="h-12" required />
              </div>
              <div className="space-y-2">
                <Label>Geburtsdatum *</Label>
                <Input data-testid="patient-dob-input" type="date" value={form.geburtsdatum} onChange={e => update('geburtsdatum', e.target.value)} className="h-12" required />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Versicherung / Krankenkasse</Label>
                <Input data-testid="patient-insurance-input" value={form.versicherung} onChange={e => update('versicherung', e.target.value)} placeholder="z.B. AOK Bayern" className="h-12" />
              </div>
              <div className="space-y-2">
                <Label>Pflegegrad</Label>
                <Select value={String(form.pflegegrad)} onValueChange={v => update('pflegegrad', parseInt(v))}>
                  <SelectTrigger className="h-12" data-testid="patient-pflegegrad-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Kein Pflegegrad</SelectItem>
                    <SelectItem value="1">Pflegegrad 1</SelectItem>
                    <SelectItem value="2">Pflegegrad 2</SelectItem>
                    <SelectItem value="3">Pflegegrad 3</SelectItem>
                    <SelectItem value="4">Pflegegrad 4</SelectItem>
                    <SelectItem value="5">Pflegegrad 5</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input data-testid="patient-address-input" value={form.adresse} onChange={e => update('adresse', e.target.value)} placeholder="Straße, PLZ Ort" className="h-12" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input data-testid="patient-phone-input" value={form.telefon} onChange={e => update('telefon', e.target.value)} placeholder="Telefonnummer" className="h-12" />
              </div>
              <div className="space-y-2">
                <Label>Team</Label>
                <Input data-testid="patient-team-input" value={form.team} onChange={e => update('team', e.target.value)} placeholder="z.B. Team Nord" className="h-12" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Diagnose(n) mit Wundbezug</Label>
              <Textarea data-testid="patient-diagnosen-input" value={form.diagnosen} onChange={e => update('diagnosen', e.target.value)} placeholder="z.B. Diabetes mellitus Typ 2, pAVK Stadium III, CVI..." rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Beginn der Wundversorgung</Label>
              <Input data-testid="patient-versorgungsbeginn-input" type="date" value={form.versorgungsbeginn} onChange={e => update('versorgungsbeginn', e.target.value)} className="h-12" />
            </div>
            <div className="space-y-2">
              <Label>Notizen</Label>
              <Textarea data-testid="patient-notes-input" value={form.notizen} onChange={e => update('notizen', e.target.value)} placeholder="Übergabenotizen..." rows={3} />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3 mt-4">
          <Button type="button" variant="outline" onClick={() => navigate(`/patienten/${id}`)} className="flex-1 h-12 rounded-xl">
            Abbrechen
          </Button>
          <Button type="submit" disabled={saving} className="flex-1 h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="save-patient-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Speichern...' : 'Änderungen speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}
